/**
 * Lead-scoped quote identity helpers.
 *
 * LeadDetailsView stays mounted across `/leads/:leadId` navigations, so a
 * previously loaded quote must not be treated as the current lead's quote.
 */

import { pickEntityId } from "../../../shared/jobJourney/buildJobJourneySteps.js";

/**
 * Quote ids stored on the lead itself (conversion / automated provisional).
 * Does not include journey-strip or previously loaded quote state.
 *
 * @param {Object|null|undefined} lead
 * @returns {string|null}
 */
export function pickLeadNativeQuoteId(lead) {
  return pickEntityId(
    lead?.convertedQuoteId,
    lead?.tracking?.conversionData?.quoteId,
    lead?.automatedPricing?.quoteId
  );
}

/**
 * @param {Object|null|undefined} quote
 * @returns {string|null}
 */
export function getQuoteLeadId(quote) {
  return pickEntityId(
    quote?.leadId,
    quote?.lead_id,
    quote?.lead?.id,
    quote?.lead?._id
  );
}

/**
 * True when this quote is safe to treat as belonging to `leadId`.
 * Quotes with no lead id are not trusted across navigations.
 *
 * @param {Object|null|undefined} quote
 * @param {string|null|undefined} leadId
 * @returns {boolean}
 */
export function quoteBelongsToLead(quote, leadId) {
  if (!quote || !leadId) return false;
  const quoteLeadId = getQuoteLeadId(quote);
  if (!quoteLeadId) return false;
  return quoteLeadId === String(leadId).trim();
}

/**
 * @param {Object|null|undefined} quote
 * @param {string|null|undefined} leadId
 * @param {{ allowMissingLeadId?: boolean }} [options]
 * @returns {Object|null}
 */
export function selectLinkedQuoteForLead(
  quote,
  leadId,
  { allowMissingLeadId = false } = {}
) {
  if (!quote || !leadId) return null;
  const quoteLeadId = getQuoteLeadId(quote);
  if (!quoteLeadId) return allowMissingLeadId ? quote : null;
  return quoteLeadId === String(leadId).trim() ? quote : null;
}

/**
 * Quote id safe to pass into estimate / journey writes for this lead.
 *
 * @param {{
 *   linkedQuote?: Object|null,
 *   lead?: Object|null,
 *   leadId?: string|null,
 * }} args
 * @returns {string|null}
 */
export function pickTrustedQuoteIdForLead({
  linkedQuote = null,
  lead = null,
  leadId = null,
} = {}) {
  const trusted = selectLinkedQuoteForLead(linkedQuote, leadId);
  const routeId = pickEntityId(leadId);
  const leadMatchesRoute =
    Boolean(routeId) && pickEntityId(lead?.id, lead?._id) === routeId;
  return pickEntityId(
    trusted?.id,
    trusted?._id,
    leadMatchesRoute ? pickLeadNativeQuoteId(lead) : null
  );
}
